import React from 'react';

import './InfoProgramModal.scss';

import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import { Button as SpectrumButton } from '@bestdoctor/spectrum-uikit/Button';
import { Divider } from '@bestdoctor/spectrum-uikit/Divider';

import { IProgram } from 'interfaces/program/programInterfaces';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { modalSubtitleInfo } from 'ui/programs/programs';
import { useLoadClinicsList } from 'hooks/programs/useLoadClinicsList';
import { useSnackbar } from 'notistack';
import { ErrorData } from 'interfaces/global/globalInterfaces';

const LuchiIcon: React.FC<{ sx?: React.CSSProperties }> = ({ sx }) => {
  const fillColor = sx?.color || 'rgba(92, 119, 125, 1)';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      style={sx}
    >
      <path
        d="M8.60512 5.38471L8.42392 1.3335H7.5742L7.393 5.38471L6.95526 5.53635L4.43088 2.36156L3.7347 2.87559L5.95676 6.23158C6.64055 7.25964 8.66616 6.67027 8.60512 5.38471Z"
        fill={fillColor}
      />
      <path
        d="M10.6765 7.77368L14.4559 6.3527L14.1985 5.53635L10.3132 6.6245L10.0271 6.24303L12.2472 2.84508L11.5797 2.36156L9.05527 5.52109C8.31808 6.45856 9.452 8.24193 10.6765 7.77368Z"
        fill={fillColor}
      />
      <path
        d="M5.97492 9.78401L3.72329 13.14L4.41852 13.654L6.9429 10.4792L7.3959 10.6299L7.57709 14.6668H8.42396L8.60516 10.6299C8.66619 9.33006 6.68541 8.71017 5.97492 9.78401Z"
        fill={fillColor}
      />
      <path
        d="M5.68784 6.62448L1.78825 5.53633L1.53076 6.35268L5.32544 7.77366L5.31018 8.24191L1.53076 9.64763L1.78825 10.464L5.7031 9.3911C6.94288 9.05826 6.92762 6.97257 5.68784 6.62448Z"
        fill={fillColor}
      />
      <path
        d="M9.05911 10.4945L11.5835 13.6417L12.2787 13.1276L10.0261 9.77164L10.3122 9.39017L14.2128 10.4631L14.4693 9.64671L10.6756 8.24099C9.45202 7.78895 8.24181 9.46455 9.05911 10.4945Z"
        fill={fillColor}
      />
    </svg>
  );
};

interface ModalProps {
  open: boolean;
  programData: IProgram;
  handleClose: () => void;
}

const ModalBlock = styled(Box)({
  marginTop: '16px',
});

const InfoProgramModal: React.FC<ModalProps> = ({ open, programData, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  const { handleLoadClinicsList } = useLoadClinicsList({
    programUuid: programData.programUuid,
    programName: programData.programTitle,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '800px',
          overflow: 'auto',
          padding: '20px 24px 24px',
          boxSizing: 'border-box',
          borderRadius: '24px',
        },
      }}
    >
      <Box className="modal-info-program__header">
        <Typography variant="h3" sx={{ fontSize: '18px' }}>Медицинская программа - {programData.programTitle}</Typography>

        <IconButton aria-label="close modal" sx={{ width: '40px', height: '40px' }} onClick={handleClose}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <ModalBlock>
        <Typography variant="h4" sx={{ fontSize: '18px', fontWeight: 400 }}>Компания</Typography>
        <Box sx={{ marginTop: '16px', fontSize: '14px' }}>{programData.companyTitle}</Box>
      </ModalBlock>

      <Box sx={{ marginTop: '16px'}}>
        <Divider />
      </Box>

      <ModalBlock>
        <Typography variant="h4" sx={{ fontSize: '18px', fontWeight: 400 }}>Объём</Typography>
        <Box sx={{ marginTop: '16px', fontSize: '14px' }}>{programData.stringServiceTypes}</Box>
      </ModalBlock>

      <Box sx={{ marginTop: '16px'}}>
        <Divider />
      </Box>

      <ModalBlock>
        <Typography variant="h4" sx={{ fontSize: '18px', fontWeight: 400 }}>Информация</Typography>
        <Box
          sx={{
            marginTop: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ width: '290px' }}>
              <Typography variant="h5" sx={{ ...modalSubtitleInfo, marginTop: 0 }}>
                Период обслуживания
              </Typography>
              <Box sx={{ fontSize: '14px', marginTop: 0 }}>{programData.period}</Box>
            </Box>

            <Box sx={{ width: '290px' }}>
              <Typography variant="h5" sx={{ ...modalSubtitleInfo, marginTop: 0 }}>
                Город
              </Typography>
              <Box sx={{ fontSize: '14px', marginTop: 0 }}>{programData.city}</Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ width: '290px' }}>
              <Typography variant="h5" sx={{ ...modalSubtitleInfo, marginTop: 0 }}>
                Количество человек
              </Typography>
              <Box sx={{ fontSize: '14px', marginTop: 0 }}>{programData.slotsCount}</Box>
            </Box>

            <Box sx={{ width: '290px' }}>
              <Typography variant="h5" sx={{ ...modalSubtitleInfo, marginTop: 0 }}>
                Полис ВЗР
              </Typography>
              <Box sx={{ fontSize: '14px', marginTop: 0 }}>{programData.isVzr ? 'Входит в программу' : 'Отсутствует'}</Box>
            </Box>
          </Box>
        </Box>
      </ModalBlock>

      <Box sx={{ marginTop: '16px'}}>
        <Divider />
      </Box>

      <ModalBlock>
        <Typography variant="h4" sx={{ fontSize: '18px', fontWeight: 400 }}>Документы</Typography>

        <Box
          sx={{
            marginTop: '16px',
            display: 'flex',
            gap: '18px',
          }}
        >
          <SpectrumButton
            variant="secondary"
            size="s"
            leftElement={<LuchiIcon sx={{ width: '16px', height: '16px', color: 'rgba(92, 119, 125, 1)' }} />}
            onClick={() => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              window.open(programData.medicalProgram);
            }}
          >
            Мед. программа
          </SpectrumButton>
          <SpectrumButton
            variant="secondary"
            size="s"
            leftElement={<FormatListBulletedIcon sx={{ width: '16px', height: '16px', color: 'rgba(92, 119, 125, 1)' }} />}
            onClick={() => {
              handleLoadClinicsList();
            }}
          >
            Список клиник
          </SpectrumButton>
        </Box>
      </ModalBlock>

      <ModalBlock
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <SpectrumButton variant="promo" size="s" onClick={handleClose}>
          Закрыть
        </SpectrumButton>
      </ModalBlock>
    </Dialog>
  );
};

export { InfoProgramModal };
